const Logger = require("../Logger");

const ALERT_VOLUME_STATE = "alert";
const DEFAULT_VOLUME_STATE = "default";

const service = {
  player: null,
  alertVolumeLevel: null,
  defaultVolumeLevel: null,
  pendingVolumeLevel: null,
  volumeControlSupported: false,
  lastSuccessfulVolumeLevel: null
};

service.configure = ( volumeControlSupported, volumePercentage, player ) => {
  return new Promise((resolve, _) => {
    if (!volumeControlSupported) {
      console.log("Volume control not supported");
      service.volumeControlSupported = false;
    } else {
      service.player = player;
      service.volumeControlSupported = true;
      service.alertVolumeLevel = volumePercentage;

      if (service.defaultVolumeLevel !== null) {
        return resolve();
      }

      if (player && player.requestVolumeLevel) {
        player.requestVolumeLevel();
      } else {
        service.volumeControlSupported = false;
        Logger.viewerError("E000000112", "requestVolumeLevel not available on player");
      }
    }
    resolve();
  })
}

const setVolumeLevel = ( type ) => {
  return new Promise((resolve, _) => {
    try {
      if (!service.volumeControlSupported) return;

      if (type === ALERT_VOLUME_STATE && service.alertVolumeLevel === null) {
        Logger.viewerError("E000000113", "Alert volume level not set, disabling volume control.");
        service.volumeControlSupported = false;
        return;
      }

      if (service.defaultVolumeLevel === null) {
        service.pendingVolumeLevel = type;
        return;
      }

      if (!service.player || !service.player.setVolumeLevel) {
        service.volumeControlSupported = false;
        Logger.viewerError("E000000113", "setVolumeLevel not available on player");
        return;
      }

      if (type === service.lastSuccessfulVolumeLevel) {
        Logger.viewerWarning(`Volume control already in the desired state (${type}); skipping`);
        return;
      }

      service.player.setVolumeLevel(
        type === ALERT_VOLUME_STATE ?
          service.alertVolumeLevel : service.defaultVolumeLevel
      );

      service.lastSuccessfulVolumeLevel = type;
      if (service.pendingVolumeLevel) service.pendingVolumeLevel = null;
    } catch (e) {
      Logger.viewerError("E000000113", `Error setting ${type} volume level`, {
        errorMessage: e.message,
      });
    } finally {
      resolve();
    }
  })
}

service.setVolumeLevelToAlert = () => {
  return setVolumeLevel( ALERT_VOLUME_STATE );
}

service.setVolumeLevelToDefault = () => {
  return setVolumeLevel( DEFAULT_VOLUME_STATE );
}

service.notifyCurrentVolumeLevel = currentVolumeLevel => {
  return new Promise((resolve, _) => {
    try {
      if (currentVolumeLevel === null || currentVolumeLevel === undefined) {
        service.volumeControlSupported = false;
        Logger.viewerError(
          "E000000113",
          "Failed to get volume level: received invalid volume level",
          { volume: currentVolumeLevel }
        );
        return;
      }

      const volumeLevel = Number(currentVolumeLevel);
      const isValidVolumeLevel = (!Number.isNaN(volumeLevel) && volumeLevel >= 0 && volumeLevel <= 100);

      if (!isValidVolumeLevel) {
        service.volumeControlSupported = false;

        Logger.viewerError(
          "E000000113",
          "Failed to get volume level: received invalid volume level",
          { volume: currentVolumeLevel }
        );
        return;
      }

      if (volumeLevel === service.alertVolumeLevel) {
        service.volumeControlSupported = false;
        Logger.viewerError(
          "E000000113",
          "Current volume percentage is the same as alert volume percentage. User should manually adjust the volume in the TV to the desired default level.",
          { volume: volumeLevel }
        );
        return;
      }

      service.defaultVolumeLevel = volumeLevel;

      if (service.defaultVolumeLevel !== null && service.pendingVolumeLevel) {
        setVolumeLevel( service.pendingVolumeLevel );
      }
    } catch (error) {
      Logger.viewerError(
        "E000000113",
        "Failed to get volume level",
        {
          errorMessage: error.message
        }
      );
    } finally {
      resolve();
    }
  })
}

service.notifyCurrentVolumeLevelReadError = (errorMessage) => {
  service.volumeControlSupported = false;
  Logger.viewerError(
    "E000000113",
    "Failed to get volume level",
    {
      errorMessage
    }
  );
}

module.exports = service;
